import type React from 'react';
import { defHalfOverlayStyles } from '@/components/utils/DefaultStyles';
import type { StoreInviteMemberGroup } from '@/common/interfaces/store-invites.interface';
import { ACCOUNT_TYPES } from '@/constants/Home';
import { getGroupOrderStoreCloseTimestamp, getOrderExpirationTime } from '@/utilities/OrderUtils';

/** Copy for the store invites entry point that replaces the share card. Desktop modal UI applies at 800px and above. */
export const STORE_INVITES_CLOSES_IN_TEXT = 'your store closes in';
export const STORE_INVITES_ENTRY_CTA_TEXT = 'Invite contacts to order from your store';
export const STORE_INVITES_ENTRY_EMOJI = '💌';
export const STORE_INVITES_MANUAL_REMINDER_BADGE_TEXT = 'Manual Reminder';
export const STORE_INVITES_ENTRY_TOGGLE_LABEL = 'Toggle reminder details';
export const STORE_INVITES_ENTRY_DETAILS_TEXT = 'Details';
export const STORE_INVITES_ENTRY_SHOW_DETAILS_LABEL = 'Show reminder details';
export const STORE_INVITES_ENTRY_HIDE_DETAILS_LABEL = 'Hide reminder details';

/** HQ card states after the organizer sends invites. */
export const STORE_INVITE_ENTRY_STATUS = {
  NOT_SENT: 'not_sent',
  REMINDERS_ON: 'reminders_on',
  REMINDERS_OFF: 'reminders_off',
} as const;

export type StoreInviteEntryStatus =
  (typeof STORE_INVITE_ENTRY_STATUS)[keyof typeof STORE_INVITE_ENTRY_STATUS];

export const STORE_INVITES_SHEET_VIEW = {
  INVITES: 'invites',
  REMINDERS: 'reminders',
} as const;

export type StoreInvitesSheetView =
  (typeof STORE_INVITES_SHEET_VIEW)[keyof typeof STORE_INVITES_SHEET_VIEW];

export const STORE_INVITES_INVITED_MEMBER_TEXT = 'contact invited';
export const STORE_INVITES_INVITED_MEMBERS_TEXT = 'contacts invited';
export const STORE_INVITES_INVITED_LIST_TITLE = 'Invited';
export const STORE_INVITES_INVITE_SENT_TEXT = 'Invite sent';
export const STORE_INVITES_INVITED_LIST_CLOSE_LABEL = 'Close invited list';
export const STORE_INVITES_REMINDER_SENDING_STATUS_TEXT = 'Sending…';
export const STORE_INVITES_REMINDER_COUNT_SINGULAR = '1 reminder sent';
export const STORE_INVITES_REMINDER_COUNT_PLURAL_SUFFIX = 'reminders sent';

/** HQ "Send Reminder Now" flow: spinner → sent, with a 24h resend confirm. */
export const STORE_INVITES_REMINDER_SEND_STEP = {
  CONFIRM_RESEND: 'confirmResend',
  SENDING: 'sending',
  SENT: 'sent',
} as const;

export type StoreInvitesReminderSendStep =
  (typeof STORE_INVITES_REMINDER_SEND_STEP)[keyof typeof STORE_INVITES_REMINDER_SEND_STEP];

export const STORE_INVITES_REMINDER_SEND_MS = 1200;
export const STORE_INVITES_REMINDER_RESEND_WINDOW_MS = 24 * 60 * 60 * 1000;
export const STORE_INVITES_REMINDER_JUST_SENT_WINDOW_MS = 60 * 60 * 1000;
export const STORE_INVITES_REMINDER_JUST_SENT_TEXT = 'You just sent a reminder';
export const STORE_INVITES_REMINDER_SENT_HOURS_AGO_PREFIX = 'You sent a reminder';
export const STORE_INVITES_REMINDER_HOUR_AGO_TEXT = '1 hour ago';
export const STORE_INVITES_REMINDER_HOURS_AGO_SUFFIX = 'hours ago';
export const STORE_INVITES_REMINDER_RESEND_BODY =
  'Are you sure you want to send another one so soon?';
export const STORE_INVITES_REMINDER_RESEND_CANCEL_TEXT = 'Cancel';
export const STORE_INVITES_REMINDER_RESEND_CONFIRM_TEXT = 'Send Anyway';
export const STORE_INVITES_REMINDER_SENT_OK_TEXT = 'OK';
export const STORE_INVITES_REMINDER_SENDING_PREFIX = 'Sending reminder to';
export const STORE_INVITES_REMINDER_SENT_PREFIX = 'Reminder sent to';
export const STORE_INVITES_REMINDER_MEMBER_NOUN = 'contact';
export const STORE_INVITES_REMINDER_MEMBERS_NOUN = 'contacts';
export const STORE_INVITES_REMINDER_RESEND_ICON = '🔁';
export const STORE_INVITES_AUTO_REMINDERS_SCHEDULE_TEXT =
  "We'll send them auto reminders 7, 3, 2, and 1 day before your store closes.";
export const STORE_INVITES_SEND_REMINDER_NOW_TEXT = 'Send Reminder Now';
export const STORE_INVITES_SEND_THEM_A_REMINDER_TEXT = 'Send Them a Reminder';
export const STORE_INVITES_SEND_MORE_INVITES_TEXT = 'Send More Invites';
export const STORE_INVITES_OPTED_OUT_PREFIX_TEXT = "You've opted out of auto reminders.";
export const STORE_INVITES_OPT_IN_LINK_TEXT = 'Tap here to opt-in right now';

/** Copy for the invites bottom sheet opened from the entry point. */
export const STORE_INVITES_SHEET_TITLE = 'Invite contacts to Order';
export const STORE_INVITES_OPEN_QUERY_PARAM = 'openStoreInvites';

/** HQ path after launch when store invites is off. */
export const getStoreOrderPath = (orderId: string | number): string => `/order/${orderId}`;

/** HQ path used after launch so the invite sheet can auto-open. */
export const getStoreInvitesOrderPath = (orderId: string | number): string =>
  `${getStoreOrderPath(orderId)}?${STORE_INVITES_OPEN_QUERY_PARAM}=true`;

/** Post-launch HQ path: open the invite sheet only when text-blast SMS is enabled. */
export const getPostStoreLaunchPath = (
  orderId: string | number,
  isStoreInvitesEnabled: boolean
): string =>
  isStoreInvitesEnabled ? getStoreInvitesOrderPath(orderId) : getStoreOrderPath(orderId);
export const STORE_INVITES_AUDIENCE_TITLE = "WHO YOU'RE INVITING";
export const STORE_INVITES_VIEW_FULL_LIST_TEXT = 'View full list ›';
export const STORE_INVITES_MEMBER_COUNT_SUFFIX = 'people';
export const STORE_INVITES_MEMBERS_ERROR_TEXT = 'Could not load store members.';
export const STORE_INVITES_MEMBERS_LOADING_TEXT = 'Loading members...';
export const STORE_INVITES_NO_MEMBERS_TEXT = 'No members found.';
/** Shown instead of the audience list once every contact on the account has been invited. */
export const STORE_INVITES_ALREADY_INVITED_TITLE =
  "You've Already Invited Everyone in Your Account";
export const STORE_INVITES_SEND_REMINDERS_TO_INVITED_TEXT = 'Send Reminders to Invited Contacts';
export const STORE_INVITES_ADD_CONTACTS_TITLE = 'Add Contacts';
export const STORE_INVITES_ADD_CONTACTS_SUBTITLE = 'You can add contacts manually or import them';
export const STORE_INVITES_ADD_MORE_TEXT = '+ Add More';
export const STORE_INVITES_SELECTED_MEMBER_TEXT = 'member selected';
export const STORE_INVITES_SELECTED_MEMBERS_TEXT = 'contacts selected';

interface IStoreInvitesAudienceOption {
  label: string;
  value: StoreInviteMemberGroup;
}

export interface IStoreInviteAudienceNouns {
  singular: string;
  plural: string;
  singularTitle: string;
  pluralTitle: string;
}

/** Collegiate and corporate HQ both say Contacts. */
export const getStoreInviteAudienceNouns = (
  accountType?: string | null
): IStoreInviteAudienceNouns => {
  const contacts: IStoreInviteAudienceNouns = {
    singular: 'contact',
    plural: 'contacts',
    singularTitle: 'Contact',
    pluralTitle: 'Contacts',
  };

  return accountType === ACCOUNT_TYPES.CORPORATE ? contacts : contacts;
};

export interface IStoreInviteCopyContext {
  storeName?: string | null;
  organization?: string | null;
  school?: string | null;
  business?: string | null;
}

const readableInviteLabel = (value?: string | null): string => {
  const trimmed = value?.trim() ?? '';
  if (!trimmed || trimmed.toUpperCase() === 'NONE') {
    return '';
  }

  return trimmed;
};

/**
 * Who the audience options name.
 * Collegiate is the org at its school. Corporate is the business. Blank falls back to generic copy.
 */
const getStoreInviteAudiencePlaceName = (
  accountType?: string | null,
  context?: IStoreInviteCopyContext
): string => {
  const organization = readableInviteLabel(context?.organization);
  const school = readableInviteLabel(context?.school);
  const business = readableInviteLabel(context?.business);

  if (accountType === ACCOUNT_TYPES.CORPORATE) {
    return business;
  }

  if (organization && school) {
    return `${organization} at ${school}`;
  }

  return organization || business;
};

export const getStoreInvitesAudienceOptions = (
  pluralTitle: string,
  placeName?: string
): IStoreInvitesAudienceOption[] => {
  const boughtFrom = placeName || 'your org';
  const accountName = placeName || 'this account';

  return [
    {
      label: `${pluralTitle} who bought from ${boughtFrom} in the last 6 months`,
      value: 'bought_last_6_months',
    },
    {
      label: `${pluralTitle} who bought from ${boughtFrom} in the last 12 months`,
      value: 'bought_last_12_months',
    },
    {
      label: `Every contact connected to ${accountName}`,
      value: 'all',
    },
  ];
};

/**
 * HQ card label before the first invite goes out.
 * Used by StoreInvitesEntry on the live store page.
 */
export const getStoreInviteEntryCta = (storeName?: string | null): string => {
  const name = readableInviteLabel(storeName);
  if (!name) {
    return STORE_INVITES_ENTRY_CTA_TEXT;
  }

  return `Invite contacts to order from ${name}`;
};

/**
 * Invite modal title: org and school for collegiate, business when there is no org.
 * Used by StoreInvitesSheet.
 */
export const getStoreInviteSheetTitle = (context?: IStoreInviteCopyContext): string => {
  const organization = readableInviteLabel(context?.organization);
  const school = readableInviteLabel(context?.school);
  const business = readableInviteLabel(context?.business);

  if (organization && school) {
    return `Invite ${organization} at ${school} to Order`;
  }

  if (organization) {
    return `Invite ${organization} to Order`;
  }

  if (business) {
    return `Invite ${business} to Order`;
  }

  return STORE_INVITES_SHEET_TITLE;
};

export interface IStoreInviteCopy extends IStoreInviteAudienceNouns {
  entryCta: string;
  invitedSingular: string;
  invitedPlural: string;
  sheetTitle: string;
  countSuffix: string;
  membersError: string;
  membersLoading: string;
  noMembers: string;
  selectedSingular: string;
  selectedPlural: string;
  addManually: string;
  addMembersTitle: string;
  addMemberButton: string;
  phoneDuplicate: string;
  membersTitle: string;
  membersAddLabel: string;
  unnamedMember: string;
  audienceOptions: IStoreInvitesAudienceOption[];
}

export const getStoreInviteCopy = (
  accountType?: string | null,
  context?: IStoreInviteCopyContext
): IStoreInviteCopy => {
  const nouns = getStoreInviteAudienceNouns(accountType);
  const { singular, plural, singularTitle, pluralTitle } = nouns;

  return {
    ...nouns,
    entryCta: getStoreInviteEntryCta(context?.storeName),
    invitedSingular: 'contact invited',
    invitedPlural: 'contacts invited',
    sheetTitle: getStoreInviteSheetTitle(context),
    countSuffix: plural,
    membersError: `Could not load store ${plural}.`,
    membersLoading: `Loading ${plural}...`,
    noMembers: `No ${plural} found.`,
    selectedSingular: `${singular} selected`,
    selectedPlural: `${plural} selected`,
    addManually: `+ Add ${plural} manually`,
    addMembersTitle: `Add ${pluralTitle}`,
    addMemberButton: `Add ${singularTitle}`,
    phoneDuplicate: `It looks like you've already added this number. Please enter another ${singular}'s details.`,
    membersTitle: pluralTitle,
    membersAddLabel: `Add ${plural} manually`,
    unnamedMember: singularTitle,
    audienceOptions: getStoreInvitesAudienceOptions(
      pluralTitle,
      getStoreInviteAudiencePlaceName(accountType, context)
    ),
  };
};

export const STORE_INVITES_AUDIENCE_OPTIONS: IStoreInvitesAudienceOption[] =
  getStoreInvitesAudienceOptions('Contacts');

export const DEFAULT_STORE_INVITES_AUDIENCE: StoreInviteMemberGroup = 'bought_last_12_months';
export const STORE_INVITES_MEMBERS_PAGE = 1;
export const STORE_INVITES_MEMBERS_LIMIT = 50;

/** Copy for the manual recipients row under the audience options. */
export const STORE_INVITES_ADD_MANUALLY_TEXT = '+ Add contacts manually';
export const STORE_INVITES_MANUAL_ADDED_SUFFIX = 'added manually';
export const STORE_INVITES_MANUAL_EDIT_TEXT = 'Edit / Add More';
export const STORE_INVITES_MANUAL_REMOVE_LABEL = 'Remove recipient';

/** Copy for the Add Contacts sheet opened from "+ Add contacts manually". */
export const STORE_INVITES_ADD_MEMBERS_TITLE = 'Add Contacts';
export const STORE_INVITES_ADD_MEMBERS_BACK_LABEL = 'Back to invite options';
export const STORE_INVITES_ADD_MEMBERS_DONE_TEXT = 'Done';
export const STORE_INVITES_ADD_MEMBER_BUTTON_TEXT = 'Add Contact';
export const STORE_INVITES_ADD_NAME_PLACEHOLDER = 'Name';
export const STORE_INVITES_NAME_MAX_LENGTH = 24;
export const STORE_INVITES_ADD_PHONE_PLACEHOLDER = '(555) 123-4567';
export const STORE_INVITES_INVITEES_TITLE = 'Invitees';
export const STORE_INVITES_NAME_REQUIRED_TEXT = 'Enter a name';
export const STORE_INVITES_PHONE_INVALID_TEXT = 'Enter a valid 10-digit US mobile number';
export const STORE_INVITES_PHONE_DUPLICATE_TEXT =
  "It looks like you've already added this number. Please enter another contact's details.";
export const STORE_INVITES_BULK_TITLE = 'Add contacts in bulk';
export const STORE_INVITES_ADD_TAB_VALUES = {
  MANUAL: 'manual',
  BULK: 'bulk',
} as const;

export type StoreInvitesAddTab =
  (typeof STORE_INVITES_ADD_TAB_VALUES)[keyof typeof STORE_INVITES_ADD_TAB_VALUES];

export const STORE_INVITES_ADD_TABS: { id: StoreInvitesAddTab; label: string }[] = [
  { id: STORE_INVITES_ADD_TAB_VALUES.MANUAL, label: 'Add Manually' },
  { id: STORE_INVITES_ADD_TAB_VALUES.BULK, label: 'Bulk Add' },
];

export const STORE_INVITES_BULK_OPTION_IDS = {
  CSV: 'csv',
  SHEET: 'sheet',
  CONTACTS: 'contacts',
  DEVICE: 'device',
} as const;

export type StoreInvitesBulkOptionId =
  (typeof STORE_INVITES_BULK_OPTION_IDS)[keyof typeof STORE_INVITES_BULK_OPTION_IDS];

export const STORE_INVITES_BULK_OPTIONS: { id: StoreInvitesBulkOptionId; label: string }[] = [
  { id: STORE_INVITES_BULK_OPTION_IDS.CSV, label: 'Upload CSV / VCF' },
  { id: STORE_INVITES_BULK_OPTION_IDS.SHEET, label: 'Import Google Sheet' },
  { id: STORE_INVITES_BULK_OPTION_IDS.CONTACTS, label: 'Import Google Contacts' },
  { id: STORE_INVITES_BULK_OPTION_IDS.DEVICE, label: 'From phone contacts' },
];

export const STORE_INVITES_ADD_MORE_MENU_TEXT = '+ Add More';
export const STORE_INVITES_ADD_MORE_CSV_TEXT = 'Upload CSV / VCF';
export const STORE_INVITES_ADD_MORE_SHEETS_TEXT = 'Google Sheets';
export const STORE_INVITES_ADD_MORE_CONTACTS_TEXT = 'Google Contacts';
export const STORE_INVITES_ADD_MORE_DEVICE_TEXT = 'From phone contacts';
export const STORE_INVITES_FILE_ACCEPT = '.csv,.vcf,.vcard,text/vcard,text/x-vcard';
export const STORE_INVITES_IMPORT_MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;
export const STORE_INVITES_IMPORT_FILE_TOO_LARGE_TEXT =
  'The file size is larger than 20MB. Please upload a smaller CSV or VCF.';
export const STORE_INVITES_BATCH_COUNT_SUFFIX = 'added';
export const STORE_INVITES_IMPORT_COUNT_PREFIX = 'Import';
export const STORE_INVITES_COLUMN_FALLBACK_PREFIX = 'Column';
export const STORE_INVITES_SHEET_EDITED_PREFIX = 'Edited';
export const STORE_INVITES_GOOGLE_MARK = 'G';
export const STORE_INVITES_ACCOUNT_INITIAL = 'S';
export const STORE_INVITES_SHEET_ICON = '▦';
export const STORE_INVITES_CONTACTS_ICON = '☺';
export const STORE_INVITES_CHECK_MARK = '✓';
export const STORE_INVITES_PICKER_GRID_ICON = '▦';
export const STORE_INVITES_PICKER_LIST_ICON = '☰';
export const STORE_INVITES_PICKER_GRID_LABEL = 'Show grid';
export const STORE_INVITES_PICKER_LIST_LABEL = 'Show list';
export const STORE_INVITES_IMPORT_SCAN_MS = 700;
export const STORE_INVITES_IMPORT_FINDING_TEXT = 'Finding contacts…';
export const STORE_INVITES_IMPORT_CONTACTS_FOUND_SUFFIX = 'contacts found';
export const STORE_INVITES_IMPORT_MISSED_BODY =
  'If you think we missed some, make sure each contact has a name and a 10-digit US mobile number.';
export const STORE_INVITES_IMPORT_INVALID_PHONES_BODY =
  'We found contacts in this file, but none had a valid 10-digit US mobile number.';
export const STORE_INVITES_IMPORT_KIND_FILE = 'file';
export const STORE_INVITES_IMPORT_KIND_SHEET = 'sheet';
export const STORE_INVITES_IMPORT_SOUNDS_GOOD_TEXT = 'Sounds Good';
export const STORE_INVITES_IMPORT_MAPPING_TITLE = "We couldn't tell which columns to use";
export const STORE_INVITES_IMPORT_MAPPING_BODY =
  "Just point us at the right ones and we'll handle the rest.";
export const STORE_INVITES_IMPORT_MAPPING_NAME_LABEL = 'Which column is the name?';
export const STORE_INVITES_IMPORT_MAPPING_PHONE_LABEL = 'Which column is the phone number?';
export const STORE_INVITES_IMPORT_MAPPING_CONTINUE_TEXT = 'Continue';
export const STORE_INVITES_IMPORT_CANCEL_TEXT = 'Cancel';
export const STORE_INVITES_IMPORT_ACCOUNT_TITLE = 'Choose an account';
export const STORE_INVITES_IMPORT_ACCOUNT_SUBTITLE = 'to continue to Store Invites';
export const STORE_INVITES_IMPORT_ACCOUNT_NAME = 'Store Owner';
export const STORE_INVITES_IMPORT_ACCOUNT_EMAIL = 'storeowner@gmail.com';
export const STORE_INVITES_IMPORT_OTHER_ACCOUNT_TEXT = 'Use another account';
export const STORE_INVITES_IMPORT_CONSENT_TITLE =
  'Store Invites wants to access your Google Account';
export const STORE_INVITES_IMPORT_CONSENT_SHEETS =
  'See and download your Google Sheets spreadsheets';
export const STORE_INVITES_IMPORT_CONSENT_CONTACTS = 'See your Google contacts';
export const STORE_INVITES_IMPORT_ALLOW_TEXT = 'Allow';
export const STORE_INVITES_IMPORT_SELECT_TEXT = 'Select';
export const STORE_INVITES_IMPORT_SEARCH_SHEETS_TEXT = 'Search spreadsheets';
export const STORE_INVITES_IMPORT_SELECT_CONTACTS_TEXT = 'Select contacts';
export const STORE_INVITES_GOOGLE_CONTACTS_BATCH_LABEL = 'Google Contacts import';
export const STORE_INVITES_GOOGLE_SHEET_BATCH_FALLBACK = 'Google Sheet import';
export const STORE_INVITES_DEVICE_CONTACTS_BATCH_LABEL = 'Phone contacts';
export const STORE_INVITES_IMPORT_SEARCH_CONTACTS_TEXT = 'Search contacts';
export const STORE_INVITES_IMPORT_CONTACTS_CONTINUE_TEXT = 'Continue';
export const STORE_INVITES_IMPORT_CONTACTS_EMPTY_TEXT =
  'No contacts with a US mobile number found.';
export const STORE_INVITES_GOOGLE_NOT_CONFIGURED_TEXT =
  'Google import is not configured yet. Upload a CSV or VCF instead.';
export const STORE_INVITES_GOOGLE_CONTACTS_DISABLED_TEXT =
  'Google Contacts import is not enabled yet. Upload a CSV or VCF instead.';
export const STORE_INVITES_GOOGLE_IMPORT_FAILED_TEXT =
  'Could not import from Google. Please try again or upload a CSV.';
export const STORE_INVITES_GOOGLE_PICKER_CANCELLED_TEXT = 'No spreadsheet selected.';
export const STORE_INVITES_DEVICE_UNSUPPORTED_TEXT =
  'Phone contacts are only available on Chrome for Android.';
export const STORE_INVITES_DEVICE_IMPORT_FAILED_TEXT =
  'Could not read phone contacts. Please try again or upload a CSV.';
export const STORE_INVITES_SAVE_ROW_LABEL = 'Save invitee';
export const STORE_INVITES_CANCEL_ROW_LABEL = 'Cancel invitee edit';
export const STORE_INVITES_REMOVE_BATCH_LABEL = 'Remove import';

export const STORE_INVITES_IMPORT_STEP = {
  IDLE: 'idle',
  ACCOUNT: 'account',
  CONSENT: 'consent',
  PICKER: 'picker',
  CONTACTS_PICKER: 'contactsPicker',
  IMPORTING: 'importing',
  PREVIEW: 'preview',
  MAPPING: 'mapping',
} as const;

export type StoreInvitesImportStep =
  (typeof STORE_INVITES_IMPORT_STEP)[keyof typeof STORE_INVITES_IMPORT_STEP];

export const STORE_INVITES_GOOGLE_FLOW = {
  SHEETS: 'sheets',
  CONTACTS: 'contacts',
} as const;

export type StoreInvitesGoogleFlow =
  (typeof STORE_INVITES_GOOGLE_FLOW)[keyof typeof STORE_INVITES_GOOGLE_FLOW];

export const STORE_INVITES_SHEET_PICKER_NAVS = [
  { id: 'recent', label: 'Recent' },
  { id: 'mydrive', label: 'My Drive' },
  { id: 'shared', label: 'Shared with me' },
] as const;

export type StoreInvitesSheetPickerNav = (typeof STORE_INVITES_SHEET_PICKER_NAVS)[number]['id'];

export const STORE_INVITES_MOCK_SHEETS = [
  { id: 's1', name: 'Customer Roster 2026', edited: '2 days ago', category: 'recent' },
  { id: 's2', name: 'Fall Season Contacts', edited: '1 week ago', category: 'recent' },
  { id: 's3', name: 'Customer Directory', edited: '3 weeks ago', category: 'mydrive' },
  { id: 's4', name: 'Alumni Contacts', edited: '2 months ago', category: 'shared' },
] as const;

export const STORE_INVITES_MOCK_GOOGLE_CONTACTS = [
  { id: 'gc1', name: 'Priya Nair', phone: '(212) 555-0148' },
  { id: 'gc2', name: 'Marcus Webb', phone: '(415) 555-0172' },
  { id: 'gc3', name: 'Elena Cho', phone: '(646) 555-0193' },
  { id: 'gc4', name: 'Devon Lang', phone: '(917) 555-0114' },
  { id: 'gc5', name: 'Sofia Ruiz', phone: '(310) 555-0186' },
  { id: 'gc6', name: 'Owen Blake', phone: '(773) 555-0129' },
];

export const STORE_INVITES_MOCK_SHEET_FIRST_NAMES = [
  'Casey',
  'Morgan',
  'Drew',
  'Jamie',
  'Taylor',
  'Peyton',
  'Quinn',
  'Reese',
];

export const STORE_INVITES_MOCK_SHEET_LAST_NAMES = [
  'Kramer',
  'Delgado',
  'Voss',
  'Higgins',
  'Marsh',
  'Osei',
  'Pruitt',
  'Cantu',
];

/** Copy and defaults for the invite message. Concatenated SMS can exceed one 160-char segment. */
export const STORE_INVITES_MESSAGE_TITLE = "MESSAGE THEY'LL GET";
export const STORE_INVITES_MESSAGE_MAX_LENGTH = 320;

export const getStoreInviteClientFirstName = (
  firstName?: string | null,
  fullName?: string | null
): string => {
  const fromFirst = (firstName ?? '').trim();
  if (fromFirst) {
    return fromFirst.split(/\s+/)[0] ?? '';
  }

  const fromFull = (fullName ?? '').trim();
  if (!fromFull) {
    return '';
  }

  return fromFull.split(/\s+/)[0] ?? '';
};

export const appendStoreInviteLink = (message: string, storeUrl?: string | null): string => {
  const trimmed = message.trim();
  if (!storeUrl) {
    return trimmed;
  }

  const normalizedUrl = storeUrl.replace(/\/$/, '');
  if (trimmed.includes(normalizedUrl) || /\/store\?id=\d+/.test(trimmed)) {
    return trimmed;
  }

  return `${trimmed}\n${normalizedUrl}`;
};

export interface IStoreInviteMessageOptions {
  organization?: string | null;
  business?: string | null;
  /** Store close date (`group_order_date`); the store closes at Eastern midnight after it. */
  closeDate?: string | null;
}

const STORE_INVITE_DEADLINE_TIMEZONE = 'America/New_York';

/** Last calendar day to order, e.g. `Oct 21`, from the Eastern close instant. */
export const formatStoreInviteDeadline = (closeDate?: string | null): string => {
  if (!closeDate) {
    return '';
  }

  const closeMs = getGroupOrderStoreCloseTimestamp(closeDate);
  if (closeMs == null) {
    return '';
  }

  // The close instant is midnight after the last order day; step back into that day.
  return new Date(closeMs - 60 * 60 * 1000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: STORE_INVITE_DEADLINE_TIMEZONE,
  });
};

const getStoreInviteOwnerName = (options?: IStoreInviteMessageOptions): string =>
  (options?.organization ?? '').trim() || (options?.business ?? '').trim();

/**
 * Launch invite: `Hey, {organizer} just launched a store for {org}. Last day to order is
 * {deadline}. Order here: {link}`. Pieces that are unknown are left out.
 */
export const getStoreInviteDefaultMessage = (
  firstName?: string | null,
  storeUrl?: string | null,
  fullName?: string | null,
  options?: IStoreInviteMessageOptions
): string => {
  const name = getStoreInviteClientFirstName(firstName, fullName);
  const org = getStoreInviteOwnerName(options);
  const deadline = formatStoreInviteDeadline(options?.closeDate);

  let launch = 'Hey, a new store just launched.';
  if (name && org) {
    launch = `Hey, ${name} just launched a store for ${org}.`;
  } else if (name) {
    launch = `Hey, ${name} just launched a store.`;
  } else if (org) {
    launch = `Hey, ${org} just launched a store.`;
  }

  const deadlineSentence = deadline ? ` Last day to order is ${deadline}.` : '';
  const message = `${launch}${deadlineSentence} Order here:`;
  const normalizedUrl = (storeUrl ?? '').replace(/\/$/, '');

  return normalizedUrl ? `${message} ${normalizedUrl}` : message;
};

export type IStoreInviteReminderMessageOptions = IStoreInviteMessageOptions;

/**
 * Handlebars-style placeholder kept in the saved reminder body. api-server swaps it for the real
 * time left when each 7/3/2/1-day reminder (or Send Reminder Now) goes out; a body without it is
 * sent as-is. The bare `X days/hours` spelling is still recognised for older saved bodies.
 */
export const STORE_INVITES_REMINDER_TIME_LEFT_TOKEN = '{{X days/hours}}';
const STORE_INVITES_REMINDER_TIME_LEFT_PATTERN =
  /\{\{\s*X\s*\[?\s*days?\s*\/\s*hours?\s*\]?\s*\}\}|\bX\s*\[?\s*days?\s*\/\s*hours?\s*\]?/i;

export const hasStoreInviteReminderTimeLeftToken = (message?: string | null): boolean =>
  Boolean(message && STORE_INVITES_REMINDER_TIME_LEFT_PATTERN.test(message));

/** Days while a full day or more remains, otherwise hours. Matches the live close countdown. */
export const getStoreInviteReminderClosesInPhrase = (closeDate?: string | null): string => {
  if (!closeDate) {
    return 'soon';
  }

  const remaining = getOrderExpirationTime(closeDate);
  const days = Number.parseInt(remaining.orderDueDays, 10);
  const hours = Number.parseInt(remaining.orderDueHours, 10);

  if (!Number.isFinite(days) || !Number.isFinite(hours) || (days <= 0 && hours <= 0)) {
    return 'soon';
  }

  if (days <= 0) {
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }

  return days === 1 ? '1 day' : `${days} days`;
};

/** Note under the reminder textarea while the placeholder is still in the message. */
export const getStoreInviteReminderTimeLeftNote = (closeDate?: string | null): string => {
  const closesIn = getStoreInviteReminderClosesInPhrase(closeDate);
  const today = closesIn === 'soon' ? '' : ` (today that's "${closesIn}")`;

  return `${STORE_INVITES_REMINDER_TIME_LEFT_TOKEN} is replaced with the actual time left when each reminder is sent${today}.`;
};

export const getStoreInviteReminderDefaultMessage = (
  firstName?: string | null,
  storeUrl?: string | null,
  fullName?: string | null,
  options?: IStoreInviteReminderMessageOptions
): string => {
  const owner =
    getStoreInviteOwnerName(options) || getStoreInviteClientFirstName(firstName, fullName);
  const storeLabel = owner ? `${owner}'s store` : 'Your store';
  const message = `${storeLabel} closes in ${STORE_INVITES_REMINDER_TIME_LEFT_TOKEN}. Order before it's too late!`;

  return appendStoreInviteLink(message, storeUrl);
};

export const STORE_INVITES_DEFAULT_MESSAGE = getStoreInviteDefaultMessage();
export const STORE_INVITES_REMINDER_DEFAULT_MESSAGE = getStoreInviteReminderDefaultMessage();

/** Copy for the send action. Send opens the reminders sheet; Sounds Good / Got It fires SMS. */
export const STORE_INVITES_SEND_CTA_TEXT = 'Send Invites';
export const STORE_INVITES_SENDING_TEXT = 'Sending…';
export const STORE_INVITES_SEND_ERROR_TEXT = 'Could not send invites. Please try again.';
export const STORE_INVITES_PROFANITY_ERROR_TEXT =
  'Please remove inappropriate language from your message before sending.';
export const STORE_INVITES_REMINDER_SAVE_ERROR_TEXT =
  'Could not update your reminder message. Please try again.';
export const STORE_INVITES_REMINDER_SEND_ERROR_TEXT = 'Could not send reminder. Please try again.';
export const STORE_INVITES_REMINDER_SAVE_MS = 500;

/** Copy for the auto-reminders sheet opened after Send Invites. */
export const STORE_INVITES_REMINDERS_TITLE = "We'll send them auto reminders";
export const STORE_INVITES_REMINDERS_BODY =
  "We'll remind them to order 7, 3, 2 & 1 day before your store closes. Reminders help you hit your goal.";
export const STORE_INVITES_REMINDER_MESSAGE_TITLE = 'REMINDER MESSAGE';
/** Answers whether an edit changes one send or the whole schedule. */
export const STORE_INVITES_REMINDER_EDIT_HINT = 'Edits update every upcoming reminder.';
export const STORE_INVITES_REMINDER_COUNTDOWN_FALLBACK = 'Store closes in X days';
export const STORE_INVITES_REMINDER_COUNTDOWN_SOON = 'Store closes soon';
export const STORE_INVITES_REMINDERS_CONFIRM_TEXT = 'Sounds Good';
export const STORE_INVITES_REMINDERS_OPT_OUT_TEXT = "I'll remind them myself";
export const STORE_INVITES_REMINDERS_OPT_OUT_TITLE = 'No auto reminders';
export const STORE_INVITES_REMINDERS_OPT_OUT_BODY =
  'You can always send auto reminders from your HQ by tapping on the "Send Reminders" button.';
export const STORE_INVITES_REMINDERS_GOT_IT_TEXT = 'Got It';
export const STORE_INVITES_REMINDERS_BACK_LABEL = 'Back to reminders';
export const STORE_INVITES_REMINDERS_CLOSE_LABEL = 'Close reminders';

/** Copy for the members list opened from "View full list". */
export const STORE_INVITES_MEMBERS_TITLE = 'Contacts';
export const STORE_INVITES_MEMBERS_SEARCH_LABEL = 'Search by name';
export const STORE_INVITES_DESELECT_ALL_TEXT = 'Deselect All';
export const STORE_INVITES_SELECT_ALL_TEXT = 'Select All';
export const STORE_INVITES_MEMBERS_SEND_NOTE = "We'll send this text to everyone on your behalf.";
export const STORE_INVITES_MEMBERS_BACK_LABEL = 'Back to invite options';
export const STORE_INVITES_MEMBERS_ADD_LABEL = 'Add contacts manually';
export const STORE_INVITES_UNNAMED_MEMBER_TEXT = 'Contact';
/** Debounce before the typed name is sent to the members API as `search`. */
export const STORE_INVITES_SEARCH_DEBOUNCE_MS = 300;

/** Copy and options for the social sharing row. */
export const STORE_INVITES_SOCIAL_TITLE = 'Or invite via social groups';
export const STORE_INVITES_SOCIAL_SHARE_LABEL = 'Share store invite';
export const STORE_INVITES_SOCIAL_ICON = '/src/assets/png/store-invites-social-share.png';
export const STORE_INVITES_LINK_COPIED_TEXT = 'Store link copied!';

/** Mobile sheets slide with translateY over 320ms ease. */
export const STORE_INVITES_SHEET_TRANSITION_MS = 320;

/** Desktop centered cards scale and fade over 200ms ease. */
export const STORE_INVITES_DESKTOP_TRANSITION_MS = 200;

/** HQ card reminder details slide open and shut over 260ms ease; matches the SCSS transition. */
export const STORE_INVITES_ENTRY_COLLAPSE_MS = 260;

/** Drag the handle farther than this to dismiss a mobile sheet. */
export const STORE_INVITES_SHEET_DRAG_CLOSE_PX = 90;

/** HQ blur applied on the dim behind any open invite sheet. */
export const STORE_INVITES_BACKDROP_BLUR = '8px';

export const STORE_INVITES_CLOSE_ICON_PATH = '/src/assets/svg/close.svg';

export const STORE_INVITES_SHEET_OVERLAY_STYLE: React.CSSProperties = {
  ...defHalfOverlayStyles,
  top: 'unset',
  bottom: 0,
  height: 'auto',
  maxHeight: '90vh',
  width: '100vw',
  margin: '0',
  padding: '0',
  zIndex: 1001,
  overflow: 'hidden',
  borderRadius: '24px 24px 0 0',
};

/** Viewport width at which the invite overlay becomes a centered modal instead of a bottom sheet. */
export const STORE_INVITES_DESKTOP_MIN_WIDTH = 800;

/** Full-viewport Overlay stage so each sheet can be an independent card. */
export const STORE_INVITES_STAGE_OVERLAY_STYLE: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  transform: 'none',
  backgroundColor: 'transparent',
  width: '100%',
  height: '100%',
  maxWidth: 'none',
  maxHeight: 'none',
  margin: 0,
  padding: 0,
  zIndex: 1001,
  overflow: 'visible',
  boxShadow: 'none',
  border: 'none',
  pointerEvents: 'none',
};

/** Centered desktop modal used at 800px and above. */
export const STORE_INVITES_DESKTOP_OVERLAY_STYLE: React.CSSProperties = {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: '#FFFFFF',
  width: '560px',
  maxWidth: 'calc(100vw - 48px)',
  maxHeight: '92vh',
  height: 'auto',
  margin: 0,
  padding: 0,
  zIndex: 1001,
  overflow: 'hidden',
  boxSizing: 'border-box',
  borderRadius: '20px',
  boxShadow: '0 18px 48px rgba(29, 32, 68, 0.24)',
  border: '1px solid transparent',
};

export const STORE_INVITES_DESKTOP_CLOSE_ICON_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  right: 0,
  padding: '23px',
  zIndex: 1001,
  cursor: 'pointer',
  lineHeight: 0,
  background: 'transparent',
};

/** View full list (Members) overlay fills 80% of the viewport. */
export const STORE_INVITES_MEMBERS_OVERLAY_HEIGHT = '80%';

export const STORE_INVITES_MEMBERS_SHEET_OVERLAY_STYLE: React.CSSProperties = {
  ...STORE_INVITES_SHEET_OVERLAY_STYLE,
  height: STORE_INVITES_MEMBERS_OVERLAY_HEIGHT,
  minHeight: STORE_INVITES_MEMBERS_OVERLAY_HEIGHT,
  maxHeight: STORE_INVITES_MEMBERS_OVERLAY_HEIGHT,
};

export const STORE_INVITES_MEMBERS_DESKTOP_OVERLAY_STYLE: React.CSSProperties = {
  ...STORE_INVITES_DESKTOP_OVERLAY_STYLE,
  height: STORE_INVITES_MEMBERS_OVERLAY_HEIGHT,
  minHeight: STORE_INVITES_MEMBERS_OVERLAY_HEIGHT,
  maxHeight: STORE_INVITES_MEMBERS_OVERLAY_HEIGHT,
};

/** Add Members hugs its content and never grows past 80% of the viewport. */
export const STORE_INVITES_ADD_MEMBERS_SHEET_OVERLAY_STYLE: React.CSSProperties = {
  ...STORE_INVITES_SHEET_OVERLAY_STYLE,
  height: 'auto',
  minHeight: 0,
  maxHeight: STORE_INVITES_MEMBERS_OVERLAY_HEIGHT,
};

export const STORE_INVITES_ADD_MEMBERS_DESKTOP_OVERLAY_STYLE: React.CSSProperties = {
  ...STORE_INVITES_DESKTOP_OVERLAY_STYLE,
  height: 'auto',
  minHeight: 0,
  maxHeight: STORE_INVITES_MEMBERS_OVERLAY_HEIGHT,
};

/** Mobile invited list is a shorter bottom sheet than the invite overlay. */
export const STORE_INVITES_INVITED_LIST_OVERLAY_STYLE: React.CSSProperties = {
  ...STORE_INVITES_SHEET_OVERLAY_STYLE,
  maxHeight: '75vh',
};

/** Desktop invited list matches the 560px invite modal, capped at 620px tall. */
export const STORE_INVITES_INVITED_LIST_DESKTOP_OVERLAY_STYLE: React.CSSProperties = {
  ...STORE_INVITES_DESKTOP_OVERLAY_STYLE,
  maxHeight: '620px',
};
